import React, { useState } from 'react';
import { Box } from '@mui/material';
import SortOutlinedIcon from '@mui/icons-material/SortOutlined';
import ArcaPdfStudioLayout from '../../components/arca/ArcaPdfStudioLayout';
import PageEditor from '../../components/arca/PageEditor';
import { appendUploadFiles, isPdfFile } from '../../components/arca/uploadItemUtils';
import { getToken } from '../../api/client';
import API_CONFIG from '../../config/api';

const MAX_FILES = 20;

function baixarBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

export default function ArcaPdfOrganizarPage() {
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleFilesAdd = (files) => {
    setError('');
    setSuccess('');
    const incoming = Array.from(files || []).filter(isPdfFile);
    if (!incoming.length) {
      setError('Selecione arquivos PDF.');
      return;
    }
    const room = Math.max(0, MAX_FILES - items.length);
    if (!room) {
      setError(`Limite de ${MAX_FILES} PDFs. Remova algum para adicionar outro.`);
      return;
    }
    const n = appendUploadFiles(incoming.slice(0, room), setItems, {
      multiple: true,
      filter: isPdfFile,
    });
    if (!n) setError('Selecione arquivos PDF.');
    else if (incoming.length > room) {
      setError(`Foram adicionados ${room} arquivo(s). Limite: ${MAX_FILES} PDFs.`);
    }
  };

  const handleApply = async (pagesSpec) => {
    if (!items.length) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const fd = new FormData();
      items.forEach((it) => fd.append('files', it.file, it.file.name));
      fd.append('pages', JSON.stringify(pagesSpec));
      const token = getToken();
      const res = await fetch(`${API_CONFIG.BACKEND_URL}/api/arca/pdf/organize`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Erro ao organizar (${res.status}).`);
      }
      const blob = await res.blob();
      const disposition = res.headers.get('Content-Disposition') || '';
      const match = disposition.match(/filename\*?=(?:UTF-8''|")?([^";]+)/i);
      const filename = match
        ? decodeURIComponent(match[1].replace(/"/g, ''))
        : `organizado-${Date.now()}.pdf`;
      baixarBlob(blob, filename);
      setSuccess('PDF organizado gerado e baixado.');
    } catch (e) {
      console.error('[ArcaPdfOrganizar]', e);
      setError(e.message || 'Erro ao organizar PDF.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <ArcaPdfStudioLayout
      title="Organizar PDF"
      info="Adicione um ou mais PDFs (já montados ou avulsos). No editor abaixo, reordene, gire, recorte ou exclua páginas de todos juntos e gere um único arquivo."
      limits={
        <>
          Limite: até <strong>{MAX_FILES}</strong> PDFs · <strong>50 MB</strong> cada. Use o{' '}
          <strong>+</strong> para incluir outro documento sem perder a organização atual.
        </>
      }
      error={error}
      onClearError={() => setError('')}
      onRotateError={setError}
      success={success}
      onClearSuccess={() => setSuccess('')}
      items={items}
      setItems={setItems}
      loading={saving}
      enableRotate={false}
      enableSortAlpha={false}
      dropzone={{
        accept: 'application/pdf,.pdf',
        multiple: true,
        maxSizeMb: 50,
        onFilesAdd: handleFilesAdd,
        label: 'Arraste PDFs ou clique para selecionar',
        hint: 'Pode juntar um PDF já montado com outros arquivos. Organize as páginas no editor abaixo.',
      }}
      actionLabel="Ir ao editor ↓"
      actionLoadingLabel="Aguarde…"
      actionStartIcon={<SortOutlinedIcon />}
      onAction={() => {
        const el = document.getElementById('arca-pdf-page-editor');
        el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }}
      actionDisabled={!items.length}
      onClear={() => {
        setItems([]);
        setSuccess('');
      }}
      belowStudio={
        items.length > 0 ? (
          <Box id="arca-pdf-page-editor" sx={{ mt: 3 }}>
            <PageEditor
              files={items.map((it) => ({ id: it.id, file: it.file }))}
              disabled={saving}
              onApply={handleApply}
              applyLabel={saving ? 'Gerando PDF…' : 'Aplicar e salvar'}
            />
          </Box>
        ) : null
      }
    />
  );
}
